// Phase-1 hygiene metrics — typed port of the v1 dashboard's METRIC_GROUPS.
//
// Source of truth for metric labels, kinds, descriptions, formulas, RAG bands,
// and recommended actions. Used by:
//   - Phase 1b-1 insights.ts (label lookup)
//   - Phase 1b-3 chart components (trend metric labels)
//   - Phase 1 sprint detail tabs (full grid + slide-in panel)
//
// Keep verbatim with v1 unless a metric definition genuinely changes upstream
// (in which case update workflows/jira_sprint_scorecard/ first, then mirror).

export type MetricKind =
  | "int"        // scalar integer (e.g. M1 SP)
  | "pct"        // scalar percentage in [0,1] (e.g. M7)
  | "days"       // scalar days
  | "bool"       // binary (M18)
  | "ratio3"    // 3-element share array, see `parts` (M4)
  | "share"     // { sp, share } object (M2, M3)
  | "spPct"     // { sp, pct } object (M10)
  | "dict";      // map from category → number (M11, M12, M20)

export type MetricSource = "field" | "changelog";

export interface MetricDetails {
  formula: string;
  whyMatters: string;
  bands: string;
  source: MetricSource;
  actions: string[];
}

export interface MetricDef {
  id: string;
  label: string;
  kind: MetricKind;
  desc: string;
  details: MetricDetails;
  /** ratio3 only: the labels for the 3 array slots, in order. */
  parts?: [string, string, string];
  /** dict only: unit suffix for the rendered values (e.g. "d" for days). */
  unit?: string;
}

export interface MetricGroup {
  title: string;
  metrics: MetricDef[];
}

export const METRIC_GROUPS: MetricGroup[] = [
  {
    title: "Delivery",
    metrics: [
      {
        id: "M1", label: "Total SP delivered", kind: "int",
        desc: "Total story points delivered (Done / Closed / Resolved at sprint end).",
        details: {
          formula: "Σ Story Points of issues whose status ∈ { Done, Closed, Resolved } at sprint end.",
          whyMatters: "Diagnostic magnitude. On its own it has no RAG band — pair it with M5 (planned) and M7 (completion %) to know whether the volume is good or bad.",
          bands: "No static band. Target: within ±10% of planned SP.",
          source: "field",
          actions: ["Compare to M5 to judge if the team over- or under-committed."],
        },
      },
      {
        id: "M6", label: "Delivered SP", kind: "int",
        desc: "Same delivered SP figure as M1, kept distinct as the numerator of the M7 completion ratio.",
        details: {
          formula: "Identical to M1 — Σ SP of issues in Done / Closed / Resolved at sprint end.",
          whyMatters: "Used as the explicit numerator of M7 (Completion %). Splitting M1/M6 makes the M7 formula auditable from raw fields.",
          bands: "No static band. Target: 85–100% of planned.",
          source: "field",
          actions: ["This is a diagnostic — focus on M7 instead."],
        },
      },
      {
        id: "M7", label: "Completion %", kind: "pct",
        desc: "Delivered SP / Planned SP. Headline reliability metric.",
        details: {
          formula: "Delivered SP ÷ Planned SP",
          whyMatters: "Tells you whether the team actually delivered what they committed to. This is the headline reliability number; every other discipline metric flows back here.",
          bands: "Green ≥ 85%   ·   Amber 70–85%   ·   Red < 70%   ·   Target 90%.",
          source: "field",
          actions: [
            "If consistently red: capacity is over-committed — cut next sprint's scope by 10–20%.",
            "If newly amber: check M16 (mid-sprint additions). Scope churn often drives completion drops.",
            "If red with low M17: the plan was OK but execution slipped — look for blockers.",
          ],
        },
      },
      {
        id: "M5", label: "Planned SP", kind: "int",
        desc: "Story points in the sprint at sprint start, derived from the changelog.",
        details: {
          formula: "Σ SP of issues whose changelog shows they were already in the sprint at startDate (i.e. NOT mid-sprint additions).",
          whyMatters: "The denominator of M7. Anchors what the team committed to before mid-sprint scope changes happened.",
          bands: "No static band. Target: aligned with team capacity.",
          source: "changelog",
          actions: ["If far above team velocity baseline, the sprint was over-planned."],
        },
      },
    ],
  },
  {
    title: "Mix",
    metrics: [
      {
        id: "M2", label: "F7 SP / share", kind: "share",
        desc: "F7-tagged work delivered, and its share of total delivered SP.",
        details: {
          formula: "Σ SP of delivered issues with Work Type = F7   ·   share = M2_sp ÷ M1.",
          whyMatters: "Shows how much of delivered work was strategic (F7) vs other categories. Programme target is ~60% F7.",
          bands: "Green ≥ 55%   ·   Amber 45–55%   ·   Red < 45%   ·   Target 60%.",
          source: "field",
          actions: [
            "If red: too much non-F7 work crowding out strategic priorities — review BAU/defect intake.",
            "If green for many sprints: validate F7 quality (M19 cycle time, escaped defects).",
          ],
        },
      },
      {
        id: "M3", label: "BAU SP / share", kind: "share",
        desc: "BAU work delivered, and its share of total delivered SP.",
        details: {
          formula: "Σ SP of delivered issues with Work Type = BAU   ·   share = M3_sp ÷ M1.",
          whyMatters: "Tracks support-and-maintenance load. Should be moderate; surging BAU squeezes F7 capacity.",
          bands: "Green ≥ 25%   ·   Amber 15–25%   ·   Red < 15%   ·   Target 30%.",
          source: "field",
          actions: ["If BAU has crept above 40%, investigate root causes (regressions, on-call load)."],
        },
      },
      {
        id: "M4", label: "Ratio (F7 / BAU / Stab)", kind: "ratio3",
        parts: ["F7", "BAU", "Stability"],
        desc: "Three-way split of delivered SP across F7 / BAU / Stability (Product Defect + Tech Debt).",
        details: {
          formula: "[F7_share, BAU_share, Stability_share] where Stability = Product Defect + Tech Debt.",
          whyMatters: "The single best chart of investment balance — does each sprint's mix match the programme intent (60/30/10)?",
          bands: "Composite — RAG is computed from distance to target ratio. Target: 60% F7 · 30% BAU · 10% Stability.",
          source: "field",
          actions: ["Persistent drift from target → renegotiate intake gates with PMs / EMs."],
        },
      },
    ],
  },
  {
    title: "Sizing",
    metrics: [
      {
        id: "M8", label: "Size-compliant %", kind: "pct",
        desc: "Share of sized issues with Story Points ≤ 8 (across all issue types).",
        details: {
          formula: "(count of sized issues with SP ≤ 8) ÷ (count of all sized issues).",
          whyMatters: "Big items hide complexity and are the #1 cause of carryover. Aim for 100% of work ≤ 8 SP.",
          bands: "Green = 100%   ·   Amber 90–100%   ·   Red < 90%   ·   Target 100%.",
          source: "field",
          actions: [
            "Any issue > 8 SP should be split during refinement, not pulled into a sprint.",
            "If chronically below 90%: refinement isn't deep enough — revisit story-shaping practice.",
          ],
        },
      },
      {
        id: "M9", label: "Size violations", kind: "int",
        desc: "Count of issues with Story Points > 8.",
        details: {
          formula: "Count of issues whose Story Points value > 8.",
          whyMatters: "The raw counterpart to M8. Each violation is a candidate to split before sprint planning.",
          bands: "Green = 0   ·   Amber 1   ·   Red ≥ 2   ·   Target 0.",
          source: "field",
          actions: ["Review each violation in the next refinement and split into ≤ 8 SP children."],
        },
      },
    ],
  },
  {
    title: "Carryover",
    metrics: [
      {
        id: "M10", label: "Carryover SP / %", kind: "spPct",
        desc: "Story points (and %) of work that was carried over from prior sprints.",
        details: {
          formula: "carryover_sp = Σ SP of issues with Carryover = Yes   ·   carryover_pct = carryover_sp ÷ Σ SP of all sprint issues.",
          whyMatters: "Carryover is the most direct symptom of over-commit / scope churn. High carryover compounds: it crowds out next sprint's planned work.",
          bands: "Green ≤ 10%   ·   Amber 10–20%   ·   Red > 20%   ·   Target 10%.",
          source: "field",
          actions: [
            "If red: short-cut some planned work next sprint to absorb carryover.",
            "Pair with M16 (mid-sprint additions) — both being red is a planning hygiene crisis.",
          ],
        },
      },
      {
        id: "M11", label: "Carryover by work type", kind: "dict",
        desc: "Carryover SP broken down by Work Type.",
        details: {
          formula: "Σ SP of carryover issues, grouped by Work Type field.",
          whyMatters: "Shows which streams accumulate spillover. F7 carryover is more painful than BAU because it slows roadmap delivery.",
          bands: "Composite — target: F7 carryover < BAU carryover.",
          source: "field",
          actions: ["If F7 dominates carryover, the F7 stream is over-loaded relative to capacity."],
        },
      },
      {
        id: "M12", label: "Carryover by priority", kind: "dict",
        desc: "Carryover SP grouped into P0/P1 vs other priorities.",
        details: {
          formula: "Σ SP of carryover issues, grouped into P0/P1 bucket vs all other priorities.",
          whyMatters: "P0/P1 carryover is the most operationally serious form of spillover. It usually means escalation paths failed during the sprint.",
          bands: "Composite — target: P0/P1 carryover SP = 0.",
          source: "field",
          actions: ["Each P0/P1 in carryover should have a written explanation in the retro."],
        },
      },
    ],
  },
  {
    title: "Priority",
    metrics: [
      {
        id: "M13", label: "P0/P1 count", kind: "int",
        desc: "Number of P0 or P1 issues in the sprint.",
        details: {
          formula: "Count of issues with priority = P0 or P1.",
          whyMatters: "Diagnostic — pair with M14 share to judge whether the urgent load is proportionate to the sprint.",
          bands: "No static band. Target: trending down sprint-over-sprint.",
          source: "field",
          actions: ["If steadily rising: investigate intake gates and on-call workload."],
        },
      },
      {
        id: "M14", label: "P0/P1 share", kind: "pct",
        desc: "P0/P1 issues as a fraction of total issues in the sprint.",
        details: {
          formula: "M13 ÷ total issue count.",
          whyMatters: "Sprints over-loaded with urgent work crowd out planned roadmap items. Persistent high share → reactive squad.",
          bands: "Green ≤ 10%   ·   Amber 10–15%   ·   Red > 15%   ·   Target 10%.",
          source: "field",
          actions: ["Sustained red: a sister team needs more capacity, or P0/P1 definition has drifted."],
        },
      },
      {
        id: "M15", label: "P0/P1 spillover", kind: "int",
        desc: "P0/P1 issues marked as carryover.",
        details: {
          formula: "Count of issues where Carryover = Yes AND priority ∈ {P0, P1}.",
          whyMatters: "The most operationally critical metric — high-priority work that DID NOT finish on time.",
          bands: "Green = 0   ·   Amber = 0   ·   Red ≥ 1   ·   Target 0.",
          source: "field",
          actions: ["Any non-zero value should be discussed in retro with a written reason."],
        },
      },
    ],
  },
  {
    title: "Discipline",
    metrics: [
      {
        id: "M16", label: "Mid-sprint additions %", kind: "pct",
        desc: "Share of issues whose changelog shows they joined the sprint after the official start.",
        details: {
          formula: "(count of issues whose Sprint changelog 'to' first contained sprint_id strictly after sprint_start) ÷ total issue count.",
          whyMatters: "The cleanest measure of scope-churn-during-sprint. High M16 mechanically lowers M7 because the team is being asked to deliver more than they planned for.",
          bands: "Green ≤ 10%   ·   Amber 10–20%   ·   Red > 20%   ·   Target 10%.",
          source: "changelog",
          actions: [
            "Tighten the rule: only P0/P1 may enter mid-sprint.",
            "If chronically red, the sprint cadence may be too long for the project's volatility.",
          ],
        },
      },
      {
        id: "M17", label: "Missing estimates %", kind: "pct",
        desc: "Share of sprint issues that have no Story Points.",
        details: {
          formula: "(count of issues with Story Points = null) ÷ total issue count.",
          whyMatters: "Unestimated issues undermine M5/M7 — you don't know how big the sprint actually is. The cheapest hygiene improvement available.",
          bands: "Green = 0   ·   Amber 0–10%   ·   Red > 10%   ·   Target 0.",
          source: "field",
          actions: ["Make 'Story Points required to enter sprint' a workflow rule in Jira."],
        },
      },
      {
        id: "M18", label: "Start readiness", kind: "bool",
        desc: "READY only if every issue at sprint start has Story Points, Priority, and Work Type set.",
        details: {
          formula: "True iff every issue in the sprint has Story Points, Priority, and Work Type set at start.",
          whyMatters: "A binary go/no-go on whether the sprint started in a properly refined state. False here usually predicts red on M7.",
          bands: "Binary — True = green, False = red. Target: True.",
          source: "field",
          actions: ["Block sprint start in the planning ceremony if M18 would be False."],
        },
      },
    ],
  },
  {
    title: "Cycle time",
    metrics: [
      {
        id: "M19", label: "Avg cycle time (days)", kind: "days",
        desc: "Average days between Start Date and End Date custom fields.",
        details: {
          formula: "Average of (End Date − Start Date) days, across delivered issues that have both fields set.",
          whyMatters: "How fast the team moves a single piece of work from start to done. Long cycle times often correlate with size violations.",
          bands: "No static band. Target: trending down sprint-over-sprint.",
          source: "field",
          actions: [
            "Pair with M8 / M9 — large items balloon cycle time.",
            "Investigate any item whose cycle time is > 2× the team median.",
          ],
        },
      },
      {
        id: "M20", label: "Cycle time by work type", kind: "dict", unit: "d",
        desc: "Average cycle time grouped by Work Type.",
        details: {
          formula: "Average cycle-time days, grouped by Work Type.",
          whyMatters: "Highlights streams that move slowest. F7 should be roughly comparable to BAU; if F7 is 2–3× BAU, refinement is too shallow.",
          bands: "Composite — target: F7 average within 1.5× BAU average.",
          source: "field",
          actions: ["If F7 > 1.5× BAU: invest in pre-sprint refinement and/or parallelism."],
        },
      },
    ],
  },
];

/** Flat list of all metric defs in declaration order (Delivery → Cycle time). */
export const ALL_METRICS: MetricDef[] = METRIC_GROUPS.flatMap((g) => g.metrics);

const _BY_ID: Record<string, MetricDef> = Object.fromEntries(ALL_METRICS.map((m) => [m.id, m]));

/** Look up a metric def by short id (e.g. "M7"). Returns null if unknown. */
export function getMetricDef(id: string): MetricDef | null {
  return _BY_ID[id] ?? null;
}

/**
 * Trend metrics — the subset shown as line charts on the project view.
 * Mirrors v1's TREND_METRICS array. M10_pct is a pseudo-id used by chart code
 * to mean "the pct half of M10's spPct envelope"; do not add to METRIC_GROUPS.
 */
export const TREND_METRIC_IDS = ["M7", "M8", "M10_pct", "M14", "M16", "M17", "M19"] as const;

/**
 * Headline metrics for the Now card on the project view (Phase 1b-1).
 * Subset of the SPA's HEADLINE_METRIC_IDS in insights.ts; they must agree.
 */
export const HEADLINE_METRIC_IDS = ["M7", "M14", "M16", "M17"] as const;
